import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { GoalForm } from '@/features/goals/goal-form/GoalForm';
import { useUpdateGoal } from '@/shared/hooks';
import { type CreateGoalFormData } from '@/features/goals/goal-form/types/goals.types';
import type { EmployeePopulatedGoalIssue } from '@momentum/types';

interface UpdateGoalModalForReviewProps {
  goal: EmployeePopulatedGoalIssue;
}

export const UpdateGoalModalForReview: React.FC<
  UpdateGoalModalForReviewProps
> = ({ goal }) => {
  const { t } = useTranslation('goals');
  const [open, setOpen] = useState(false);
  const updateGoalMutation = useUpdateGoal();

  const initialValues: CreateGoalFormData = useMemo(() => {
    // Handle assignees - could be string[] or populated objects
    const assigneeIds = Array.isArray(goal.assignees)
      ? goal.assignees.filter(Boolean).map((assignee) => assignee._id)
      : [];

    return {
      ...goal,
      assignees: assigneeIds,
      creator: goal.creator._id,
      keyResults: goal.keyResults ?? [],
    };
  }, [goal]);

  const handleSubmit = async (formData: CreateGoalFormData) => {
    const { creator, ...updateData } = formData;
    await updateGoalMutation.mutateAsync({
      id: goal._id,
      data: updateData,
    });

    // Close modal on success
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent size="lg" className="flex flex-col max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {t('updateGoalTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <GoalForm
            initialValues={initialValues}
            isPending={updateGoalMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('updateGoal')}
            submittingLabel={t('updating')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
